import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/layout/Sidebar/Sidebar';
import apiClient from '../../services/api';
import { Star, MessageCircle, Send, Filter, Calendar, ChevronDown, Check } from 'lucide-react';
import { formatImgUrl } from '../../utils/imageUtils';
import './Reviews.css';

const Reviews = () => {
    const [reviews, setReviews] = useState([]);
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [replyText, setReplyText] = useState({});
    const [isSubmitting, setIsSubmitting] = useState({});
    const [filterRating, setFilterRating] = useState('All');
    const [filterStatus, setFilterStatus] = useState('All');

    useEffect(() => {
        fetchReviews();
    }, []);

    const fetchReviews = async () => {
        setIsLoading(true);
        try {
            // Get user's entity first
            const entityRes = await apiClient.get('/users/me/entity');
            const entity = entityRes.data.data;
            
            if (entity) {
                const typeMap = { 'hotel': 'Hotel', 'property': 'Property', 'car': 'Car', 'tour': 'Tour' };
                const eType = typeMap[entity.entityType] || 'Property';
                const res = await apiClient.get(`/reviews/${eType}/${entity.id}`);
                setReviews(res.data.data.reviews || []);
                setStats(res.data.data.stats);
            }
        } catch (err) {
            console.error("Failed to load reviews:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReplySubmit = async (reviewId) => {
        if (!replyText[reviewId]?.trim()) return;

        setIsSubmitting({ ...isSubmitting, [reviewId]: true });
        try {
            await apiClient.patch(`/reviews/${reviewId}/reply`, { reply: replyText[reviewId] });
            // Update local state
            setReviews(reviews.map(r => 
                r.id === reviewId ? { ...r, reply: replyText[reviewId], reply_at: new Date() } : r
            ));
            setReplyText({ ...replyText, [reviewId]: '' });
            alert('Reply sent successfully!');
        } catch (err) {
            console.error("Failed to submit reply:", err);
            alert('Failed to send reply.');
        } finally {
            setIsSubmitting({ ...isSubmitting, [reviewId]: false });
        }
    };

    const filteredReviews = reviews.filter(r => {
        const ratingMatch = filterRating === 'All' || Math.floor(r.rating) === parseInt(filterRating);
        const statusMatch = filterStatus === 'All' || 
                           (filterStatus === 'Replied' && r.reply) || 
                           (filterStatus === 'Pending' && !r.reply);
        return ratingMatch && statusMatch;
    });

    return (
        <div className="reviews-page-container">
            <Sidebar />
            <main className="main-content">
                <div className="reviews-header">
                    <div>
                        <h1>Guest Reviews</h1>
                        <p>Manage your reputation and respond to guest feedback.</p>
                    </div>
                </div>

                {/* Stats Overview */}
                {stats && (
                    <div className="reviews-stats-grid">
                        <div className="review-stat-card primary">
                            <div className="stat-main">
                                <div className="rating-big">{stats.avg_rating}</div>
                                <div className="stars-row">
                                    {[1, 2, 3, 4, 5].map(s => (
                                        <Star key={s} size={20} fill={s <= Math.round(stats.avg_rating) ? "#FFD700" : "none"} color="#FFD700" />
                                    ))}
                                </div>
                            </div>
                            <p>Average Guest Rating</p>
                            <span className="stat-count">Based on {stats.total} reviews</span>
                        </div>
                        <div className="review-stat-card">
                            <h4>Cleanliness</h4>
                            <ProgressBar value={stats.avg_cleanliness * 20} label={stats.avg_cleanliness} color="#3b82f6" />
                            <h4>Service</h4>
                            <ProgressBar value={stats.avg_service * 20} label={stats.avg_service} color="#10b981" />
                        </div>
                        <div className="review-stat-card">
                            <h4>Value</h4>
                            <ProgressBar value={stats.avg_value * 20} label={stats.avg_value} color="#f59e0b" />
                            <h4>Location</h4>
                            <ProgressBar value={stats.avg_location * 20} label={stats.avg_location} color="#ef4444" />
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="reviews-filter-bar">
                    <div className="filter-item">
                        <Star size={16} />
                        <select value={filterRating} onChange={(e) => setFilterRating(e.target.value)}>
                            <option value="All">All Ratings</option>
                            <option value="5">5 Stars</option>
                            <option value="4">4 Stars</option>
                            <option value="3">3 Stars</option>
                            <option value="2">2 Stars</option>
                            <option value="1">1 Star</option>
                        </select>
                    </div>
                    <div className="filter-item">
                        <Check size={16} />
                        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                            <option value="All">All Status</option>
                            <option value="Pending">Waiting for Reply</option>
                            <option value="Replied">Replied</option>
                        </select>
                    </div>
                </div>

                {/* Reviews List */}
                <div className="reviews-list">
                    {isLoading ? (
                        <div className="loading-state">Loading your reviews...</div>
                    ) : filteredReviews.length > 0 ? (
                        filteredReviews.map(review => (
                            <div key={review.id} className="review-item-card">
                                <div className="review-user-info">
                                    <img src={review.reviewer_image ? formatImgUrl(review.reviewer_image) : `https://api.dicebear.com/7.x/avataaars/svg?seed=${review.reviewer_name}`} alt={review.reviewer_name} />
                                    <div className="user-text">
                                        <h4>{review.reviewer_name}</h4>
                                        <p><Calendar size={12} /> {new Date(review.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <div className="review-rating-tag">
                                        <Star size={14} fill="#FFD700" color="#FFD700" />
                                        <span>{review.rating}</span>
                                    </div>
                                </div>
                                
                                <div className="review-body">
                                    <h3 className="review-title">{review.title}</h3>
                                    <p className="review-text">{review.body}</p>
                                </div>

                                {review.reply ? (
                                    <div className="review-reply-box existing">
                                        <div className="reply-header">
                                            <MessageCircle size={16} />
                                            <span>Your Response • {new Date(review.reply_at).toLocaleDateString()}</span>
                                        </div>
                                        <p>{review.reply}</p>
                                    </div>
                                ) : (
                                    <div className="review-reply-action">
                                        <textarea 
                                            placeholder="Write a professional response to this guest..."
                                            value={replyText[review.id] || ''}
                                            onChange={(e) => setReplyText({...replyText, [review.id]: e.target.value})}
                                        />
                                        <button 
                                            className="reply-btn"
                                            disabled={isSubmitting[review.id]}
                                            onClick={() => handleReplySubmit(review.id)}
                                        >
                                            {isSubmitting[review.id] ? 'Sending...' : 'Send Reply'}
                                            <Send size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="no-reviews">
                            <MessageCircle size={48} />
                            <p>No reviews found matching your criteria.</p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

const ProgressBar = ({ value, label, color }) => (
    <div className="progress-container">
        <div className="progress-labels">
            <span>{label} / 5</span>
        </div>
        <div className="progress-track">
            <div className="progress-fill" style={{ width: `${value}%`, backgroundColor: color }}></div>
        </div>
    </div>
);

export default Reviews;
